import api from "$utils/api";
import { getArrayFromArrayOrPaginated, userToMember } from "$utils/apiHelpers";
import type {
  CommunityNotifications,
  CommunityPermission,
  Flag,
  SpaceGroup as ApiSpaceGroup,
  PatchedCommunityFull,
  SpaceGroupPermissions,
  Community as ApiCommunity,
} from "@outverse-video/outverse-api-client-lib";
import type { Community, FullAccessCommunity, Member, Role } from "app/models/community";
import { derived, writable, get } from "svelte/store";
import type { ForumSpace } from "app/models/forums";
import { CommunityFlags } from "models/flags";
import { apiReady, session } from "$stores/auth";
import { localStorageStore } from "./localStorage";
import type { GenericSpace } from "app/models/spaces";
import { rolePreview } from "app/models/community";
import { PREVENT_POLLING } from "$utils/consts";
import { reportError } from "$utils/errors";

// to record a users pricing plan selection from marketing pages
export const pricingPlan = localStorageStore("pricingPlanTracking", "");

const SPACE_GROUP_REFRESH_MS = PREVENT_POLLING ? 100000000 : 15000; // 15s

interface Fetching {
  fetched: boolean;
}

export interface SpaceGroup extends Omit<ApiSpaceGroup, "spaces"> {
  spaces: Array<GenericSpace>;
  myPermissions?: SpaceGroupPermissions;
}

export interface CommunityState extends FullAccessCommunity, Fetching {
  slug: string;
  contentLibraryTitle: string;
  accessType: "full" | "restricted";
  uuid: string;
  notificationSubscriptions: CommunityNotifications;
  code?: string;
  codeRefreshedAt?: string;
  codeLoading: boolean;
  afterJoinedLink?: string;
  myPermissions: CommunityPermission[];
  permissionsFetched: boolean;
  members: Member[];
  membersIndex: { [uuid: string]: Member };
  team: Member[];
  admins: Member[];
  roles: Role[];
  // @TODO think about keeping this state more local
  roleBeingEdited?: Role;
  backgroundColorDark?: string | null;
  zendeskIntegrationEnabled?: boolean;
  intercomIntegrationEnabled?: boolean;
  zendeskChatEnabled?: boolean;
  salesforceIntegrationEnabled?: boolean;
}

export interface SpaceState extends Fetching {
  community: string;
  defaultGroup: SpaceGroup;
  groups: SpaceGroup[];
  spaceGroupsEdit: boolean;
  spaces: { [key: string]: GenericSpace };
}

async function fetchCommunitySpaceGroups(communitySlug: string) {
  const resp = await api.communitiesSpaceGroupsList({ communitySlug });

  return getArrayFromArrayOrPaginated(resp.data);
}

const spaceInitialState: SpaceState = {
  fetched: false,
  community: "",
  defaultGroup: {
    spaceGroup: {
      spaces: [],
    },
  } as any,
  groups: [],
  spaces: {},
  spaceGroupsEdit: false,
};
const spaceStore = () => {
  let spaceGroupInterval: NodeJS.Timeout | null;
  const { subscribe, set, update } = writable<SpaceState>({ ...spaceInitialState }, () => () => {
    // IMPORTANT
    // This was causing a bug in e2e tests, so commented out.
    // spaceGroupInterval && clearInterval(spaceGroupInterval);
    // Turns out it may have been necessary to prevent multiple polling intervals from being created when hot reloading.
    if (import.meta.hot) {
      if (spaceGroupInterval) {
        clearInterval(spaceGroupInterval);
      }
    }
  });
  let spaceGroupsEdit = false;
  /** Update space groups/spaces with data, notifications and perms
   *
   * this could be made simpler by a fully annotated serialiser like forums
   *
   * These have to happen together so as not to blank parts of the interface
   * such as notifications / permissions.
   *
   */
  const updateSpaceGroups = async (communitySlug: string) => {
    await apiReady;
    try {
      const [spaceGroups, groupPermsResponse, notificationsResponse] = await Promise.all([
        fetchCommunitySpaceGroups(communitySlug),
        api.communitiesSpaceGroupsPermsList({ communitySlug }),
        api.communitiesSpaceGroupsNotificationsList({ communitySlug }),
      ]);
      update((state) => {
        if (spaceGroupsEdit) return state;
        // update spaces, notifications, perms in one go
        // maybe this should just be one api call?

        const groups = spaceGroups as any as SpaceGroup[];
        const groupPerms = getArrayFromArrayOrPaginated(groupPermsResponse.data);
        const groupsWithNotifications = getArrayFromArrayOrPaginated(notificationsResponse.data);

        const groupMap: { [key: string]: SpaceGroup } = {};
        for (const group of groups) {
          groupMap[group.uuid] = group;
          // update spaces
          for (const space of group.spaces) {
            // @ts-ignore - this is due to api issue
            state.spaces[space.uuid] = space;
          }
        }

        // update group perms
        for (const groupPerm of groupPerms) {
          const group = groupMap[groupPerm.spaceGroupUuid];
          if (!group) {
            console.log(`Group ${groupPerm.spaceGroupUuid} not found`);
            continue;
          }

          groupMap[groupPerm.spaceGroupUuid].myPermissions = groupPerm.myPermissions;
        }

        if (!groupsWithNotifications) {
          console.log("notifications failed");
        } else {
          for (const group of groupsWithNotifications) {
            for (const space of group.spaceGroup.spaces) {
              const updatedForum = space as ForumSpace;
              const existingForum = state.spaces[updatedForum.uuid] as ForumSpace;
              if (!existingForum) {
                console.log(`Forum ${updatedForum.uuid} not found in existing forums`);
                continue;
              }
              existingForum.isSubscribedToActivity = updatedForum.isSubscribedToActivity;
              existingForum.newActivity = updatedForum.newActivity;
              existingForum.highValueNewRepliesCount = updatedForum.highValueNewRepliesCount;
              existingForum.highValueNewThreadsCount = updatedForum.highValueNewThreadsCount;
              existingForum.directRepliesCount = updatedForum.directRepliesCount;

              existingForum.newActivityCount =
                updatedForum.highValueNewRepliesCount +
                updatedForum.directRepliesCount +
                updatedForum.highValueNewThreadsCount;
            }
          }
        }

        return {
          ...state,
          groups: groups.filter((sg) => !sg.isDefault),
          defaultGroup: groups.find((sg) => sg.isDefault)!,
          community: communitySlug,
          fetched: true,
        };
      });
    } catch (e) {
      reportError(e as Error);
      return;
    }
  };
  return {
    subscribe,
    update,
    set,
    init: (communitySlug: string) => {
      set({ ...spaceInitialState, community: communitySlug });
    },
    hydrate: (groups: ApiSpaceGroup[]) => {
      // @ts-ignore this data gets filled in immediately afterwards (forums)
      //  better to actually replace spaces with an id list
      update((state) => {
        if (!groups) {
          console.log("notifications failed");
          return state;
        }
        for (const group of groups) {
          for (const space of group.spaces) {
            // @ts-ignore - this is due to api issue
            state.spaces[space.uuid] = space;
          }
        }
        return {
          ...state,
          groups: groups.filter((group) => !group.isDefault),
          defaultGroup: groups.find((group) => group.isDefault)!,
        };
      });
    },
    updateSpaceGroups,
    poll: async (communitySlug: string) => {
      if (spaceGroupInterval) {
        clearInterval(spaceGroupInterval);
        spaceGroupInterval = null;
      }
      updateSpaceGroups(communitySlug);
      spaceGroupInterval = setInterval(() => {
        updateSpaceGroups(communitySlug);
      }, SPACE_GROUP_REFRESH_MS);
    },
    fetch: async (communitySlug: string) => {
      updateSpaceGroups(communitySlug);
    },
    resumeFetch: (communitySlug: string) => {
      spaceGroupInterval = setInterval(() => {
        updateSpaceGroups(communitySlug);
      }, SPACE_GROUP_REFRESH_MS);
      spaceGroupsEdit = false;
    },
    cancelFetch: () => {
      if (spaceGroupInterval) {
        clearInterval(spaceGroupInterval);
        spaceGroupInterval = null;
        spaceGroupsEdit = true;
      }
    },
    /** Create a new space group with a title */
    createSpaceGroup: async (communitySlug: string, title: string) => {
      return await api.communitiesSpaceGroupsCreate({
        communitySlug: communitySlug,
        spaceGroup: { title: title } as any,
      });
    },
    /** Edit a space group
     *
     * Only the title is editable for now, (moving happens elsewhere)
     */
    editSpaceGroup: async (communitySlug: string, uuid: string, title: string) => {
      return await api.communitiesSpaceGroupsPartialUpdate({
        communitySlug,
        uuid,
        patchedSpaceGroup: {
          title,
        },
      });
    },
    deleteSpaceGroup: async (communitySlug: string, uuid: string) => {
      return await api.communitiesSpaceGroupsDestroy({
        communitySlug,
        uuid,
      });
    },
    updateSpace: (updatedSpace: GenericSpace) => {
      update((state) => {
        for (const [i, sg] of state.groups.entries()) {
          const index = sg.spaces.findIndex((el) => el.uuid === updatedSpace.uuid);
          if (index >= 0) {
            sg.spaces.splice(index, 1, updatedSpace);
            state.groups[i] = { ...state.groups[i] };
          }
        }

        return {
          ...state,
        };
      });
    },
    visitSpace: (uuid: string) => {
      update((state) => {
        (state.spaces[uuid] as ForumSpace).newActivity = false;
        return state;
      });
    },
  };
};

const initialState: CommunityState = {
  fetched: false,
  permissionsFetched: false,
  contentLibraryTitle: "Content Library",
  name: "",
  uuid: "",
  gtmId: "",
  accessType: "restricted",
  logoImage: "",
  coverImage: "",
  slug: "",
  primaryColor: "",
  widgetAccentColor: "",
  widgetButtonText: "",
  communitySectionName: "",
  memberCount: 0,
  isCommunityEnabled: false,
  isPrivate: false,
  docsVisible: false,
  notificationSubscriptions: {
    isSubscribedToNewMemberEvent: false,
  },
  code: "",
  codeLoading: true,
  members: [],
  admins: [],
  team: [],
  membersIndex: {},
  myPermissions: [],
  roles: [],
  flags: [],
  changelogVisible: false,
  isChangelogEnabled: false,
  isChangelogExpanded: true,
  changelogDescription: "",
};

const communityStore = () => {
  const { subscribe, set, update } = writable<CommunityState>({
    ...initialState,
  });

  return {
    subscribe,
    update,
    set,
    create: async (community: { name: string; primaryColor: string; isPrivate: boolean }) => {
      try {
        const { data } = await api.communitiesCreate({
          communityCreation: community,
        });
        update((state) => ({
          ...state,
          ...data,
        }));
      } catch {
        return false;
      }
      return true;
    },
    hydrate: async (data: ApiCommunity, permissions: CommunityPermission[]) => {
      update((community) => ({
        ...community,
        ...data,
        admins: [],
        members: [],
        membersIndex: {},
        slug: data.slug!,
        fetched: true,
        myPermissions: permissions,
        permissionsFetched: true,
      }));
    },
    fetch: async (communitySlug: string) => {
      update((state) => ({ ...state, slug: communitySlug }));
      await apiReady;
      api
        .communitiesNotificationSubscriptions({ slug: communitySlug })
        .then((resp) =>
          update((state) => ({
            ...state,
            notificationSubscriptions: resp.data,
          }))
        )
        .catch(() => console.log("failed to load notification subscriptions"));

      if (get(session).isAuthed) {
        api
          .communitiesRolesList({ communitySlug })
          .then((resp) =>
            resp.data.map((role) => ({
              color: "#000000",
              ...role,
              name: role.name || "New role",
              permsGroupings: role.permsGroupings || [],
            }))
          )
          .then((roles) => community.setRoles(roles))
          .catch(() => console.log("failed to load roles"));
      }
    },
    fetchMembers: async () => {
      const { slug } = get({ subscribe });
      const api = await apiReady;

      // Await the response from the API call
      const resp = await api.communitiesMembersList({ communitySlug: slug });

      const admins: Member[] = [];
      const team: Member[] = [];
      const members: Member[] = [];
      const membersIndex: { [uuid: string]: Member } = {};

      for (const user of getArrayFromArrayOrPaginated(resp.data)) {
        const member = userToMember(user);
        membersIndex[user.uuid] = member;
        members.push(member);
        if (member.roles?.find((role) => role.isAdmins)) {
          admins.push(member);
        }
        if (member.roles?.find((role) => role.isTeam)) {
          team.push(member);
        }
      }

      // Update the store once after all asynchronous operations are completed
      update((community) => ({
        ...community,
        members,
        admins,
        team,
        membersIndex,
      }));
    },
    clear: () => {
      set({ ...initialState });
    },
    updateCommunity: (communityPatch: PatchedCommunityFull) => {
      return api
        .communitiesPartialUpdate({
          slug: get(community).slug!,
          patchedCommunityFull: communityPatch,
        })
        .then((resp) =>
          update((state) => ({
            ...state,
            ...resp.data,
          }))
        );
    },
    updateMember: (members: Member[]) => {
      update((state) => {
        const prevMembers = state.members.filter((m) => !members.find((el) => el.uuid === m.uuid));
        return { ...state, members: [...prevMembers, ...members] };
      });
    },
    addAdmin: (member: Member) => {
      let previousState: Partial<CommunityState>;

      update((state) => {
        previousState = {
          admins: [...state.admins],
          team: [...state.team],
        };
        const newTeam = state.team.map((m) => {
          if (m.uuid === member.uuid) {
            const newRoles = [...(m.roles || []), rolePreview(get(adminRole)!)];
            return { ...m, roles: [...newRoles] };
          } else {
            return m;
          }
        });
        return {
          ...state,
          admins: [...state.admins, member],
          team: [...newTeam],
        };
      });
      return api
        .roleAddMember({
          uuid: get(adminRole)!.uuid,
          patchedUserId: {
            id: member.uuid,
          },
        })
        .catch((error) => {
          update((state) => ({ ...state, ...previousState }));
          throw error;
        });
    },
    removeAdmin: (member: Member) => {
      let previousState: Partial<CommunityState>;

      update((state) => {
        previousState = {
          admins: [...state.admins],
          team: [...state.team],
        };

        const newAdmins = state.admins.filter((m) => m.uuid !== member.uuid);
        const newTeam = state.team.map((m) => {
          if (m.uuid === member.uuid) {
            const newRoles = (m.roles || []).filter((r) => !r.isAdmins);
            return { ...m, roles: [...newRoles] };
          } else {
            return m;
          }
        });
        return { ...state, admins: [...newAdmins], team: [...newTeam] };
      });
      return api
        .roleRemoveMember({
          uuid: get(adminRole)!.uuid,
          patchedUserId: {
            id: member.uuid,
          },
        })
        .catch((error) => {
          update((state) => ({ ...state, ...previousState }));
          throw error;
        });
    },
    removeTeamMember: (member: Member) => {
      let previousState: Partial<CommunityState>;

      update((state) => {
        previousState = {
          admins: [...state.admins],
          team: [...state.team],
        };

        const newTeam = state.team.filter((m) => m.uuid !== member.uuid);
        const newAdmins = state.admins.filter((m) => m.uuid !== member.uuid);
        return { ...state, team: [...newTeam], admins: [...newAdmins] };
      });
      return api
        .roleRemoveMember({
          uuid: get(teamRole)!.uuid,
          patchedUserId: {
            id: member.uuid,
          },
        })
        .catch((error) => {
          update((state) => ({ ...state, ...previousState }));
          throw error;
        });
    },
    setRoles: (roles: Role[], editedRole?: Role) => {
      community.update((state) => ({
        ...state,
        roles,
        roleBeingEdited: editedRole,
      }));
    },
    setCommunity: (community: Partial<CommunityState>) => {
      update((state) => ({
        ...state,
        ...community,
      }));
    },
    setCommunityNotifications: (value: boolean) => {
      update((state) => ({
        ...state,
        notificationSubscriptions: {
          ...state.notificationSubscriptions,
          isSubscribedToNewMemberEvent: value,
        },
      }));
    },
    refreshInviteCode: () => {
      let communitySlug;
      update((community) => {
        communitySlug = community.slug;
        return community;
      });
      if (!communitySlug) return;
      update((community) => ({ ...community, codeLoading: true }));
      api.communitiesInviteCodeRefresh({ communitySlug }).then((resp) =>
        update((community) => ({
          ...community,
          code: resp.data.code,
          codeRefreshedAt: resp.data.modified,
          codeLoading: false,
        }))
      );
    },
    getInviteCode: async () => {
      update((community) => {
        api.communitiesInviteCodeGet({ communitySlug: community.slug }).then((resp) => {
          update((community) => ({
            ...community,
            code: resp.data.code,
            codeRefreshedAt: resp.data.modified,
          }));
        });

        return community;
      });
    },
    getMemberEmail: async (memberUuid: string) => {
      const resp = await api.communitiesMembersGetEmail({
        communitySlug: get(community).slug!,
        uuid: memberUuid,
      });

      update((community) => {
        console.log("resp", resp);
        const member = community.members.find((m) => m.uuid === memberUuid);
        if (member) {
          member.email = resp.data.email;
        }
        return community;
      });
      // return resp.data.email;
    },
  };
};

const communitiesStore = () => {
  interface CommunitiesState extends Fetching {
    communityList: Community[];
  }

  const initialState: CommunitiesState = { communityList: [], fetched: false };

  const { subscribe, set, update } = writable<CommunitiesState>({
    ...initialState,
  });
  return {
    subscribe,
    update,
    set,
    fetch: async () => {
      try {
        const resp = await api.communitiesList();
        set({
          communityList: getArrayFromArrayOrPaginated(resp.data),
          fetched: true,
        });
      } catch (e: any) {
        console.log("failed to fetch communities list", e);
      }
    },
  };
};

export const community = communityStore();
export const communities = communitiesStore();
export const spaces = spaceStore();

export const subUnsubForumActivity = async (forumSpace: ForumSpace) => {
  if (forumSpace.isSubscribedToActivity) {
    await api.forumSpaceUnsubscribeFromActivity({ uuid: forumSpace.uuid });
    forumSpace.isSubscribedToActivity = false;
  } else {
    await api.forumSpaceSubscribeToActivity({ uuid: forumSpace.uuid });
    forumSpace.isSubscribedToActivity = true;
  }
};

/* -- DERIVED VALUES -- */

// community derived values
export const isFetched = derived(community, ($community) => $community.fetched);
export const permissionsFetched = derived(community, ($community) => $community.permissionsFetched);
export const members = derived(community, ($community) => $community.members);
export const membersIndex = derived(community, ($community) => $community.membersIndex);
export const namedMembers = derived(members, ($members) => $members.filter((m) => m.handle));
export const admins = derived(community, ($community) => $community.admins);
export const teamMembers = derived(community, ($community) => $community.team);
export const adminUuids = derived(admins, ($admins) => new Set($admins.map((admin) => admin.uuid)));
export const isPrivateCommunity = derived(community, ($community) => $community.isPrivate);
export const logoImage = derived(community, ($community) => $community.logoImage);
export const coverImage = derived(community, ($community) => $community.coverImage);
export const isAskAiTeamOnly = derived(community, ($community) => $community.isAskAiTeamOnly);
export const backgroundColorDark = derived(
  community,
  ($community) => $community.backgroundColorDark
);

export const inviteCode = derived(community, ($community) => ({
  code: $community.code,
  codeRefreshedAt: $community.codeRefreshedAt,
  loading: $community.codeLoading,
}));
export const communitySlug = derived(community, ($community) => $community.slug!);
export const primaryColor = derived(community, ($community) => $community.primaryColor);
export const widgetAccentColor = derived(community, ($community) => $community.widgetAccentColor);
export const widgetButtonText = derived(community, ($community) => $community.widgetButtonText);
export const widgetAgentName = derived(community, ($community) => $community.widgetAgentName);
export const widgetLogo = derived(community, ($community) => $community.widgetLogo);

export const permissions = derived(community, ($community) => $community.myPermissions);

// community flags
export const communityFlags = derived(community, ($community) => $community.flags);
export const hasFlag = (flags: Flag[], flag: string) => flags?.some((f) => f.name === flag);
// Here is an example of how to use the communityFlags store:
export const hasExampleCommunityFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.EXAMPLE)
);
export const knowledgeBaseFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.KNOWLEDGE_BASE)
);
export const knowledgeBaseUIFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.KNOWLEDGE_BASE_UI)
);
export const communityFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.COMMUNITY)
);
export const semanticFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.SEMANTIC)
);

export const searchV2Flag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.SEARCH_V2)
);
export const versionHistoryFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.VERSION_HISTORY)
);
export const analyticsFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.ANALYTICS)
);
export const askAIFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.ASK_AI)
);
export const fullPagePrototypeFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.FULL_PAGE_PROTOTYPE)
);
export const multiModalAgentFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.MULTI_MODAL_AGENT_FLAG)
);

export const searchAIFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.SEARCH_AI)
);

export const trustedWorkspaceFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.TRUSTED_WORKSPACE)
);

export const intentsFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.INTENTS)
);

export const zendeskFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.ZENDESK)
);

export const zendeskInsightsFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.ZENDESK_INSIGHTS)
);

export const debugSourcesFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.DEBUG_SOURCES)
);

export const categoryClickNavigationFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.CATEGORY_CLICK_NAVIGATION)
);
export const whiteLabelFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.WHITE_LABEL)
);
export const documentFeedbackFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.DOCUMENT_FEEDBACK)
);
export const changelogFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.CHANGELOG)
);
export const sandboxFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.AI_SANDBOX)
);

export const docsWidgetFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.DOCS_WIDGET_FLAG)
);

export const docsWidgetTeamOnlyFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.DOCS_WIDGET_TEAM_ONLY_FLAG)
);

export const sandboxMarketingFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.SANDBOX_MARKETING)
);

export const integrationsFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.INTEGRATIONS)
);

export const reasoningFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.REASONING_FLAG)
);

export const conversationFeedbackFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.CONVERSATION_FEEDBACK)
);

export const escalationButtonConversationFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.ESCALATION_BUTTON_CONVERSATION)
);

export const escalationButtonHomeFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.ESCALATION_BUTTON_HOME)
);

export const escalationFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.ESCALATION)
);

export const zendeskAgentHistoryFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.ZENDESK_AGENT_HISTORY)
);

export const allIntegrationsVisibleFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.ALL_INTEGRATIONS_VISIBLE)
);

export const sessionAnalyticsV2Flag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.SESSION_ANALYTICS_V2)
);

export const errorLogsFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.ERROR_LOGS)
);

export const triggerTestingFlag = derived(communityFlags, ($communityFlags) =>
  hasFlag($communityFlags, CommunityFlags.TRIGGER_TESTING)
);

// community settings
export const externalLinks = derived(community, ($community) => {
  const links = [];
  if ($community.isExternalLinkShown && $community.externalLinkTag && $community.externalLinkUrl) {
    links.push({
      tag: $community.externalLinkTag,
      url: $community.externalLinkUrl,
      image: $community.externalLinkImage,
    });
  }
  if (
    $community.isExternalLink2Shown &&
    $community.externalLink2Tag &&
    $community.externalLink2Url
  ) {
    links.push({
      tag: $community.externalLink2Tag,
      url: $community.externalLink2Url,
      image: $community.externalLink2Image,
    });
  }
  if (
    $community.isExternalLink3Shown &&
    $community.externalLink3Tag &&
    $community.externalLink3Url
  ) {
    links.push({
      tag: $community.externalLink3Tag,
      url: $community.externalLink3Url,
      image: $community.externalLink3Image,
    });
  }
  return links;
});

export const isCommunityEnabled = derived(community, ($community) => $community.isCommunityEnabled);

export const isChangelogEnabled = derived(community, ($community) => $community.isChangelogEnabled);

export const isContentLibraryEnabled = derived(
  community,
  ($community) => $community.isContentLibraryEnabled
);

export const isDocumentsEnabled = derived(community, ($community) => $community.isDocumentsEnabled);

export const contentLibraryTitle = derived(
  community,
  ($community) => $community.contentLibraryTitle
);

export const isMemberDirectoryShown = derived(
  community,
  ($community) => $community.isMemberDirectoryShown
);

export const isSemanticRelationShown = derived(
  community,
  ($community) => $community.isSemanticRelationShown
);

// roles
export const roles = derived(community, ($community) => $community.roles);
export const rolesIndex = derived(roles, ($roles) => {
  const index: { [uuid: string]: Role } = {};
  for (const role of $roles) {
    index[role.uuid] = role;
  }
  return index;
});
export const adminRole = derived(roles, ($roles) => $roles.find((role) => role.isCommunityAdmins));
export const teamRole = derived(roles, ($roles) => $roles.find((role) => role.isCommunityTeam));
export const nonTeamRoles = derived(roles, ($roles) =>
  $roles.filter((role) => !role.isCommunityAdmins && !role.isCommunityTeam)
);
export const roleBeingEdited = derived(community, ($community) => $community.roleBeingEdited);

// spaces derived values
export const isSpacesFetched = derived(spaces, ($spaces) => $spaces.fetched);
export const spaceList = derived(spaces, ($spaces) =>
  $spaces.groups.reduce(
    (prevGroup, curGroup: SpaceGroup) => prevGroup.concat(curGroup.spaces),
    $spaces.defaultGroup.spaces
  )
);
// space groups derived values
export const isSpaceGroupsFetched = derived(spaces, ($spaces) => $spaces.fetched);
export const spaceGroups = derived(spaces, ($spaces) => $spaces.groups);
export const defaultSpaceGroup = derived(spaces, ($spaces) => $spaces.defaultGroup);

export const refreshAllCommunityData = (communitySlug: string) => {
  community.fetch(communitySlug);
  community.fetchMembers();
};

// communities derived values
export const isCommunitiesListFetched = derived(
  communities,
  ($communities) => $communities.fetched
);
